import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { FooterButton } from '../FooterButton/FooterButton';
import { EULA_URL, PRIVACY_POLICY_URL } from '../PayButton/PayButton';
import { AppchargeIcon } from '../../../../../../assets/icons';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { generateZendeskSupportURL } from './support-url.utils';
import { getIsSupercellTemplate } from '../../../../../../utils/get-is-supercell-template';
import { openNewTab } from '../../../../../../utils/open-new-tab';

interface FormFooterProps {
  isHiddenApmsShown: boolean;
  showSubscriptionAgreement?: boolean;
}

const IS_SUPERCELL_TEMPLATE = getIsSupercellTemplate();

export function FormFooter({ isHiddenApmsShown, showSubscriptionAgreement }: FormFooterProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { publisherId, publisherData } = useAppConfig();

  const { checkoutData } = useCheckout();

  const supportURL = generateZendeskSupportURL({
    checkoutData,
    publisherId,
    publisherData,
  });

  return (
    <Box className={`form-footer ${isHiddenApmsShown ? 'minimize' : ''}`} fontSize={10} pt={2}>
      <Stack direction="column" alignItems="center" spacing={1.5}>
        <Box display="flex" alignItems={'center'}>
          <Box pr={1}>{t('poweredBy')}</Box> <AppchargeIcon />
        </Box>
        <Stack direction="row" spacing={1} divider={<hr style={{ width: 'auto' }} />}>
          <FooterButton url={PRIVACY_POLICY_URL} text={t('privacyPolicy')} />
          <FooterButton url={'https://appcharge.com/refund-policy/'} text={t('refundPolicy')} />
          {!IS_SUPERCELL_TEMPLATE && <FooterButton url={supportURL} text={t('support')} />}
        </Stack>
        {showSubscriptionAgreement && <SubscriptionAgreement />}
      </Stack>
    </Box>
  );
}

// Bottom line of the subscriptions footer (ACDEV-26078), per the legal
// template: By purchasing you agree to the Appcharge 'EULA' and 'Privacy Policy'
function SubscriptionAgreement() {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  return (
    <Box
      textAlign={'center'}
      fontSize={'12px'}
      color={'#323241'}
      data-testid="subscription-agreement-disclaimer"
    >
      <Box component={'span'}>{t('subscriptionAgreementPrefix')}</Box>{' '}
      <Box component={'span'} style={{ cursor: 'pointer' }} onClick={() => openNewTab(EULA_URL)}>
        {`'${t('endUserLicenseAgreement')}'`}
      </Box>{' '}
      <Box component={'span'}>{t('and')}</Box>{' '}
      <Box
        component={'span'}
        style={{ cursor: 'pointer' }}
        onClick={() => openNewTab(PRIVACY_POLICY_URL)}
      >
        {`'${t('privacyPolicy')}'`}
      </Box>
    </Box>
  );
}
